/**
 * Chart Coordinate & Logical Index Extrapolation.
 *
 * Provides bidirectional time <-> coordinate conversion that extends beyond
 * natively indexed candles into future whitespace and historical gaps.
 */

export interface TimeScaleApi {
  timeToCoordinate(time: number): number | null;
  coordinateToTime(x: number): number | null;
  logicalToCoordinate(logical: number): number | null;
  coordinateToLogical(x: number): number | null;
}

/**
 * Extrapolates a logical index to a timestamp (in seconds) based on intervalSec.
 */
export function extrapolateLogicalToTime(
  logical: number | null | undefined,
  allCandles: Array<{ time: number }> | null | undefined,
  intervalSec: number,
): number | null {
  if (
    logical == null ||
    !allCandles ||
    !allCandles.length ||
    !intervalSec ||
    intervalSec <= 0
  ) {
    return null;
  }
  const lastIndex = allCandles.length - 1;
  if (logical > lastIndex) {
    return (
      allCandles[lastIndex].time + Math.round(logical - lastIndex) * intervalSec
    );
  }
  if (logical < 0) {
    return allCandles[0].time + Math.round(logical) * intervalSec;
  }
  return null;
}

/**
 * Extrapolates a timestamp to a fractional logical index based on intervalSec.
 */
export function extrapolateTimeToLogical(
  time: number | null | undefined,
  allCandles: Array<{ time: number }> | null | undefined,
  intervalSec: number,
): number | null {
  if (
    time == null ||
    !allCandles ||
    !allCandles.length ||
    !intervalSec ||
    intervalSec <= 0
  ) {
    return null;
  }
  const lastIndex = allCandles.length - 1;
  const lastTime = allCandles[lastIndex].time;
  const firstTime = allCandles[0].time;

  if (time > lastTime) {
    return lastIndex + (time - lastTime) / intervalSec;
  }
  if (time < firstTime) {
    return 0 - (firstTime - time) / intervalSec;
  }

  // Binary search for candle at or just before `time` to handle gaps
  let lo = 0;
  let hi = lastIndex;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (allCandles[mid].time <= time) lo = mid;
    else hi = mid - 1;
  }
  // A bar occupies one SLOT of `intervalSec` starting at its time, so a time inside the slot maps
  // linearly across the bar (a 3m anchor inside a 15m bar). Cap at ONE slot: a time in a gap (weekend,
  // overnight, or a daily bar's midnight viewed on an intraday chart) must land at the next real bar,
  // not intervalSec-many bars beyond it (DRAW-TF-1: ~170 bars off for midnight on a 180s chart).
  return lo + Math.min((time - allCandles[lo].time) / intervalSec, 1);
}

/**
 * Logical index -> x, for FRACTIONAL indices too.
 *
 * lightweight-charts 4.1.3 `logicalToCoordinate` returns a real, linear x for any INTEGER index (in range,
 * in future whitespace, before the first bar) but exactly 0 for ANY non-integer — even 592.001 (measured in
 * the real page). x is linear in the index, so interpolate between the two neighbouring integer coordinates.
 * Returns null if either neighbour cannot be resolved (never a bogus 0).
 */
export function logicalToCoordinateInterpolated(
  timeScale: TimeScaleApi,
  logical: number,
): number | null {
  const base = Math.floor(logical);
  const frac = logical - base;
  const x0 = timeScale.logicalToCoordinate(base);
  if (x0 === null) return null;
  if (frac === 0) return x0;
  const x1 = timeScale.logicalToCoordinate(base + 1);
  if (x1 === null) return null;
  return x0 + frac * (x1 - x0);
}

/**
 * Converts X screen coordinate to timestamp, falling back to logical extrapolation in whitespace.
 */
export function xToTimeWithExtrapolation(
  timeScale: TimeScaleApi | null | undefined,
  x: number,
  allCandles: Array<{ time: number }> | null | undefined,
  intervalSec: number,
): number | null {
  if (!timeScale) return null;
  const native = timeScale.coordinateToTime(x);
  if (native !== null) return native;
  if (!allCandles || !allCandles.length) return null;

  const logical = timeScale.coordinateToLogical(x);
  if (logical === null) return null;

  return extrapolateLogicalToTime(logical, allCandles, intervalSec);
}

/**
 * Converts timestamp to X screen coordinate, falling back to logical extrapolation in whitespace.
 */
export function timeToXWithExtrapolation(
  timeScale: TimeScaleApi | null | undefined,
  time: number,
  allCandles: Array<{ time: number }> | null | undefined,
  intervalSec: number,
): number | null {
  if (!timeScale) return null;
  const native = timeScale.timeToCoordinate(time);
  if (native !== null) return native;
  if (!allCandles || !allCandles.length) return null;

  const logical = extrapolateTimeToLogical(time, allCandles, intervalSec);
  if (logical === null) return null;

  return logicalToCoordinateInterpolated(timeScale, logical);
}
